import { useState } from 'react';
import { Plus } from 'lucide-react';
import Button from 'components/crm/ui/Button';
import Spinner from 'components/crm/ui/Spinner';
import DrawnGlyph from 'components/crm/ui/emptyState/DrawnGlyph';
import EmptyState from 'components/crm/ui/emptyState/EmptyState';
import useText from 'hooks/useText';
import { pickSuggestions, upcomingText } from 'pages/prospection/callListItems';

const NO_UPCOMING = { callbacks: 0, retries: 0 };

/**
 * The right column once nothing is left to call, as a card: the end of the
 * day's list, or an empty one. It suggests prospects from the global list;
 * `onAdd` returns a Promise, and the button waits for it.
 */
const SidePanel = ({ day, state, prospects, now, onAdd }) => {
  const { text, plural } = useText('prospection');
  const [adding, setAdding] = useState(null);
  const finished = state === 'finished';

  // Suggestion subtitles name a base key that `plural` completes.
  const subtitleText = ({ key, count, values }) =>
    count === undefined ? text(key, values) : plural(key, count, values);
  // The end-of-session keys are already complete: `count` is only a value.
  const upcomingLine = () => {
    const { key, count, values } = upcomingText(day?.upcoming || NO_UPCOMING);
    return text(key, count === undefined ? values : { count, ...values });
  };

  const suggestions = prospects ? pickSuggestions(prospects, now) : [];
  const hasProspects =
    (day?.activeProspects || 0) > 0 || (day?.made || []).length > 0;

  const add = (prospect) => {
    setAdding(prospect.company.id);
    Promise.resolve(onAdd(prospect))
      .catch(() => {})
      .finally(() => setAdding(null));
  };

  const renderSuggestions = () => {
    if (prospects === null || prospects === undefined) {
      return (
        <div className="py-6">
          <Spinner />
        </div>
      );
    }
    if (!suggestions.length) {
      return (
        <EmptyState
          compact
          glyph={<DrawnGlyph name={hasProspects ? 'squareCheck' : 'inbox'} />}
          title={text(
            hasProspects ? 'side.noSuggestTitle' : 'side.noProspectTitle',
          )}
          description={text(
            hasProspects ? 'side.noSuggestText' : 'side.noProspectText',
          )}
        />
      );
    }
    return suggestions.map(({ prospect, subtitle }) => (
      <div
        key={prospect.company.id}
        className="flex items-center gap-3 border-b border-line py-3"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">
            {prospect.company.name || '-'}
          </p>
          <p className="text-xs text-muted">{subtitleText(subtitle)}</p>
        </div>
        <Button
          variant="secondary"
          icon={Plus}
          disabled={adding === prospect.company.id}
          onClick={() => add(prospect)}
        >
          {text('side.add')}
        </Button>
      </div>
    ));
  };

  return (
    <section className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-y-auto rounded-2xl border border-line bg-white p-6">
      <div>
        <h2 className="font-title text-lg font-semibold leading-6 text-ink">
          {text(finished ? 'side.finishedTitle' : 'side.readyTitle')}
        </h2>
        <p className="mt-1 text-sm text-muted">
          {finished ? upcomingLine() : text('side.readyText')}
        </p>
      </div>
      <div>
        <p className="border-b border-line pb-2 text-sm font-semibold text-ink">
          {text('side.suggested')}
        </p>
        {renderSuggestions()}
      </div>
    </section>
  );
};

export default SidePanel;
